var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MediaScreenFacetType } from "flavr-common";
import { usersService } from "../multiplayer/UsersService";
import { DoubleSide, FrontSide, Group, ImageLoader, LinearFilter, Material, Mesh, MeshBasicMaterial, PlaneGeometry, SRGBColorSpace, Texture, VideoTexture } from "three";
import { RenderOrder } from "../rendering/RenderOrder";
import { FacetRenderer, isRenderableType, Renderable, RenderableUpdateTriggerState } from "../rendering/renderable/Renderable";
import { ModelMediaVideoInvalidMessage, ModelMediaVideoResizedMessage } from "../../messages/media";
import { MeshRenderable } from "../rendering/mesh/MeshRenderable";
import { peerService, remoteStreamingService, StreamType, localStreamingService } from "../multiplayer/peer";
const GEOMETRY = new PlaneGeometry(1, 1);
const imageLoader = new ImageLoader();
imageLoader.setCrossOrigin("anonymous");
const NO_SIGNAL_TEXTURE = new Texture();
imageLoader.load("img/no-signal.png", (img) => {
    NO_SIGNAL_TEXTURE.image = img;
    NO_SIGNAL_TEXTURE.needsUpdate = true;
});
/**
 * A MediaScreenRenderable displays either a local video stream of this user or a remote video stream of another peer on
 * a model. It is used for screen sharing.
 */
export class MediaScreenRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.isDestroyed = false;
        this.updateStreamFn = this.updateStream.bind(this);
        /// Stream handler methods ///
        this.onPause = () => {
            this.cleanupMedia();
        };
        this.onPlay = () => {
            if ((this.facet === undefined) || (this.texture === undefined))
                return;
            this.material.side = this.facet.params.doubleSided ? DoubleSide : FrontSide;
            this.material.map = this.texture;
            this.material.needsUpdate = true;
            this.scaleMeshToVideoAspectRatio();
        };
        this.onResize = () => {
            var _a, _b;
            if (this.texture === undefined)
                return;
            const videoElement = this.texture.image;
            if (!(videoElement instanceof HTMLVideoElement))
                return;
            this.owner.renderer
                .getConnection()
                .send(new ModelMediaVideoResizedMessage(this.owner.modelUID, (_a = videoElement.videoWidth) !== null && _a !== void 0 ? _a : videoElement.width, (_b = videoElement.videoHeight) !== null && _b !== void 0 ? _b : videoElement.height));
        };
        this.onStreamInactive = () => {
            if (this.stream === undefined)
                return;
            console.info(`[mediascreen] Video stream inactive ${this.stream.id}`);
            this.cleanupMedia();
        };
        this.onTrackEnded = () => {
            console.info(`[mediascreen] Video track ended`);
            this.cleanupMedia();
        };
        this.onTrackRemoved = () => {
            console.info(`[mediascreen] Video track removed`);
            this.cleanupMedia();
        };
        this.material = new MeshBasicMaterial({
            map: NO_SIGNAL_TEXTURE,
            transparent: false,
            side: DoubleSide
        });
        this.applyTextureToRenderable();
        remoteStreamingService.onUpdateDelegate.add(this.updateStreamFn);
        localStreamingService.onUpdateDelegate.add(this.updateStreamFn);
        void this.updateStream();
    }
    /// Getter and setter ///
    /**
     * Returns whether this user is sharing the screen
     */
    isMine() {
        return this.ownerClientId === usersService.getLocalUser().clientId;
    }
    setVisible(visible) {
        if (this.group !== undefined) {
            this.group.visible = visible;
        }
    }
    /// FacetRenderer lifecycle methods ///
    onInit() {
        this.startValidationTimer();
    }
    updateFacet(facet) {
        if (facet.params.disableMesh) {
            this.destroyScreenMesh();
        }
        else {
            this.createScreenMesh();
        }
    }
    updateTransform() {
        if (this.group !== undefined) {
            this.group.matrix.copy(this.owner.matrixWorld);
            this.group.updateMatrixWorld(true);
        }
    }
    destroy() {
        this.isDestroyed = true;
        this.stopValidationTimer();
        this.destroyScreenMesh();
        this.cleanupMedia();
        this.destroyVideoElement();
        if (this.material.map !== null)
            this.material.map.dispose();
        if (this.material.lightMap !== null)
            this.material.lightMap.dispose();
        if (this.material.envMap !== null)
            this.material.envMap.dispose();
        this.material.dispose();
    }
    _destroy() {
        remoteStreamingService.onUpdateDelegate.remove(this.updateStreamFn);
        localStreamingService.onUpdateDelegate.remove(this.updateStreamFn);
    }
    onOtherRenderableChanged(state, renderable) {
        switch (state) {
            case RenderableUpdateTriggerState.Changed:
                if (renderable instanceof Renderable) {
                    this.applyTextureToRenderable();
                }
                break;
            case RenderableUpdateTriggerState.Destroyed:
                break;
        }
    }
    /// Media methods ///
    setMedia(clientId, stream) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.stream !== undefined) {
                if (this.stream.id === stream.id) {
                    return;
                }
                else {
                    console.warn("[mediascreen] Adding new stream to existing media screen", this.stream, stream);
                    this.cleanupMedia();
                }
            }
            this.stream = stream;
            this.texture = yield this.createVideoTexture(stream);
            this.ownerClientId = clientId;
            this.applyTextureToRenderable();
        });
    }
    cleanupMedia() {
        this.material.map = NO_SIGNAL_TEXTURE;
        this.material.needsUpdate = true;
        this.unloadVideoElement();
        if (this.texture !== undefined) {
            if (this.texture.image instanceof HTMLVideoElement) {
                try {
                    this.texture.image.pause();
                }
                catch (e) {
                    console.error('[mediascreen] Error while stopping video stream', e);
                }
                this.texture.image.srcObject = null;
                this.texture.image.load(); // load triggered to remove video
                this.texture.image.onpause = null;
                this.texture.image.onplay = null;
                this.texture.image.onended = null;
                this.texture.image.onemptied = null;
                this.texture.image.onerror = null;
                this.texture.image.onabort = null;
                this.texture.image.onreset = null;
                this.texture.image.onresize = null;
            }
            this.texture.dispose();
            this.texture = undefined;
        }
        if (this.stream !== undefined) {
            this.stream.removeEventListener("inactive", this.onStreamInactive);
            this.stream.removeEventListener("removetrack", this.onTrackRemoved);
            for (const track of this.stream.getVideoTracks()) {
                track.removeEventListener("ended", this.onTrackEnded);
            }
            this.stream = undefined;
        }
        this.destroyVideoElement();
        this.ownerClientId = undefined;
        this.applyTextureToRenderable();
        this.scaleMeshToVideoAspectRatio();
    }
    /// Stream methods ///
    isStreamActive() {
        if (this.stream === undefined)
            return false;
        for (const track of this.stream.getVideoTracks()) {
            if (track.readyState !== "live") {
                return false;
            }
        }
        return this.stream.active;
    }
    getLocalStream() {
        var _a;
        let streams;
        if (this.isScreenShare()) {
            streams = localStreamingService.getScreenStreams();
        }
        else {
            streams = localStreamingService.getPlayer3DViewStreams();
        }
        return (_a = streams.find((s) => {
            return s.surfaceUid === this.owner.modelUID;
        })) === null || _a === void 0 ? void 0 : _a.stream;
    }
    getRemoteStream() {
        let streams;
        if (this.isScreenShare()) {
            streams = remoteStreamingService.getScreenStreams();
        }
        else {
            streams = remoteStreamingService.getPlayer3dViewStreams();
        }
        return streams.find((s) => {
            if (s.type === StreamType.screen || s.type === StreamType.player3dView) {
                return s.surfaceUid === this.owner.modelUID;
            }
            else {
                return false;
            }
        });
    }
    updateStream() {
        return __awaiter(this, void 0, void 0, function* () {
            const localStream = this.getLocalStream();
            const remoteStream = this.getRemoteStream();
            // Either this user streams to all peers or receives a stream from another peer
            if ((localStream !== undefined) && (remoteStream !== undefined)) {
                console.error("[mediascreen] Both local and remote streams are active for the same surfaceUid. This should not happen.");
                this.cleanupMedia();
                return;
            }
            if (localStream === undefined && remoteStream === undefined) {
                console.debug("[mediascreen] Both local and remote streams are empty.");
                this.cleanupMedia();
            }
            else {
                if (localStream !== undefined) {
                    yield this.setMedia(usersService.getLocalUser().clientId, localStream);
                }
                else if (remoteStream !== undefined) {
                    yield this.setMedia(remoteStream.clientId, remoteStream.stream);
                }
                else {
                    this.cleanupMedia();
                }
            }
        });
    }
    /// Video element creation and destruction ///
    initializeVideoElement() {
        if (this.videoElement !== null && this.videoElement !== undefined)
            return this.videoElement;
        this.videoElement = document.createElement("video");
        this.videoElement.autoplay = true;
        this.videoElement.playsInline = true;
        this.videoElement.muted = true;
        this.videoElement.style.display = "none";
        document.body.appendChild(this.videoElement);
        return this.videoElement;
    }
    unloadVideoElement() {
        if (this.videoElement === undefined || this.videoElement === null)
            return;
        if (this.videoElement.paused)
            return;
        if (!this.isVideoPlaying(this.videoElement))
            return;
        try {
            this.videoElement.pause();
            this.videoElement.srcObject = null;
            this.videoElement.load();
        }
        catch (e) {
            console.error('[mediascreen] Error while pausing video', e);
        }
    }
    destroyVideoElement() {
        if (this.videoElement === undefined || this.videoElement === null)
            return;
        this.videoElement.onpause = null;
        this.videoElement.onplay = null;
        this.videoElement.onended = null;
        this.videoElement.onerror = null;
        this.videoElement.onabort = null;
        this.videoElement.onreset = null;
        this.videoElement.onresize = null;
        this.videoElement.onmousedown = null;
        document.body.removeChild(this.videoElement);
        this.unloadVideoElement();
        this.videoElement = undefined;
    }
    /// Video texture methods ///
    createVideoTexture(stream) {
        return __awaiter(this, void 0, void 0, function* () {
            const videoElement = this.initializeVideoElement();
            videoElement.id = `mediaScreen-${stream.id}`;
            const texture = new VideoTexture(videoElement);
            texture.minFilter = LinearFilter;
            texture.magFilter = LinearFilter;
            texture.colorSpace = SRGBColorSpace;
            videoElement.onpause = this.onPause;
            videoElement.onplay = this.onPlay;
            videoElement.onended = () => {
                document.body.removeChild(videoElement);
                this.cleanupMedia();
            };
            videoElement.onerror = (error) => {
                console.error(`[mediascreen] Video element error: ${JSON.stringify(error)}`);
                this.cleanupMedia();
            };
            videoElement.onabort = () => {
                console.error(`[mediascreen] Video element abort`);
                this.cleanupMedia();
            };
            videoElement.onreset = () => {
                console.error(`[mediascreen] Video element reset`);
                this.cleanupMedia();
            };
            videoElement.addEventListener("resize", this.onResize);
            stream.addEventListener("inactive", this.onStreamInactive, { once: true });
            stream.addEventListener("removetrack", this.onTrackRemoved, { once: true });
            for (const track of stream.getVideoTracks()) {
                track.addEventListener("ended", this.onTrackEnded, { once: true });
            }
            // Not sure why, but when waiting for the video element here, then it won't work
            videoElement.srcObject = stream;
            void videoElement.play();
            this.onResize();
            return texture;
        });
    }
    getCurrentUsedTexture() {
        return this.texture === undefined || this.ownerClientId === undefined ? NO_SIGNAL_TEXTURE : this.texture;
    }
    applyTextureToRenderable() {
        const renderable = this.owner.renderable;
        if (renderable === undefined) {
            console.warn('[mediascreen] Renderable is undefined');
            return;
        }
        if (isRenderableType(MeshRenderable, renderable)) {
            this.applyTextureToMesh(renderable);
        }
        else {
            console.error("[mediascreen] MediaScreenRenderable: Wrong renderable type");
        }
    }
    applyTextureToMesh(meshRenderable) {
        if (this.facet === undefined)
            return;
        if (!meshRenderable.meshInstance.isLoaded)
            return;
        const texture = this.getCurrentUsedTexture();
        const applyOnlyTexture = this.facet.params.applyOnlyTexture;
        meshRenderable.meshInstance.traverseMaterial(Material, (mat, mesh) => {
            if (applyOnlyTexture) {
                if ("map" in mat) {
                    ;
                    mat.map = texture;
                    mat.needsUpdate = true;
                }
            }
            else {
                mesh.material = this.material;
            }
        });
    }
    /// Screen creation and destruction ///
    createScreenMesh() {
        if (this.mesh !== undefined)
            return;
        this.mesh = new Mesh(GEOMETRY, this.material);
        this.mesh.matrixAutoUpdate = false;
        this.mesh.renderOrder = RenderOrder.VIEW_TO_TEXTURE;
        if (this.group === undefined) {
            this.group = new Group();
            this.group.visible = false;
            this.group.matrixAutoUpdate = false;
            this.owner.renderer.addToScene(this.group, this.owner.modelUID);
        }
        this.group.add(this.mesh);
    }
    destroyScreenMesh() {
        if (this.mesh !== undefined) {
            if (this.mesh.parent !== null) {
                this.mesh.parent.remove(this.mesh);
            }
            this.mesh = undefined;
        }
        if (this.group !== undefined) {
            this.owner.renderer.removeFromScene(this.group, this.owner.modelUID);
            this.group = undefined;
        }
    }
    /// Timer helper methods ///
    // TODO: Check if we still need getMediaSourceStatus and startValidationTimer - They only send a message to scripts if the video is invalid
    /**
     * Returns if the media source has enough resources for video playback
     * - HAVE_NOTHING	0	No information is available about the media resource.
     * - HAVE_METADATA	1	Enough of the media resource has been retrieved that the metadata attributes are initialized. Seeking will no longer raise an exception.
     * - HAVE_CURRENT_DATA	2	Data is available for the current playback position, but not enough to actually play more than one frame.
     * - HAVE_FUTURE_DATA	3	Data for the current playback position as well as for at least a little bit of time into the future is available (in other words, at least two frames of video, for example).
     * - HAVE_ENOUGH_DATA	4	Enough data is available—and the download rate is high enough—that the media can be played through to the end without interruption.
     */
    getMediaSourceStatus() {
        if (this.stream === undefined)
            return 0;
        if (this.videoElement === null || this.videoElement === undefined)
            return 1;
        if (this.videoElement.readyState < 2)
            return 2;
        if (this.videoElement.paused)
            return 3;
        if (!this.isStreamActive())
            return 4;
        return 0;
    }
    startValidationTimer() {
        this.stopValidationTimer();
        this.validationTimer = self.setTimeout(() => {
            if (this.isDestroyed)
                return;
            const errorCode = this.getMediaSourceStatus();
            if (errorCode > 0) {
                const message = new ModelMediaVideoInvalidMessage(this.owner.modelUID, errorCode);
                this.owner.renderer.getConnection().send(message);
                if (this.ownerClientId !== undefined && this.isMine() && (peerService.peerMgr !== undefined)) {
                    peerService.peerMgr.sendToBusMessage(this.ownerClientId, message);
                }
            }
            this.startValidationTimer();
        }, 5000);
    }
    stopValidationTimer() {
        if (this.validationTimer === undefined)
            return;
        clearTimeout(this.validationTimer);
    }
    /// Other helper methods ///
    isScreenShare() {
        var _a;
        return ((_a = this.facet) === null || _a === void 0 ? void 0 : _a.params.type) === MediaScreenFacetType.screenShare;
    }
    isVideoPlaying(video) {
        return (video &&
            video.readyState >= 3 && // HAVE_FUTURE_DATA
            !video.paused &&
            !video.ended &&
            video.currentTime > 0);
    }
    scaleMeshToVideoAspectRatio() {
        const texture = (this.texture != null) ? this.texture : this.material.map;
        let scaleX = 1;
        let scaleY = 1;
        if (texture !== null && texture.image instanceof HTMLVideoElement) {
            const width = texture.image.videoWidth;
            const height = texture.image.videoHeight;
            const aspect = width / height;
            if (width > height) {
                scaleY = 1 / aspect;
            }
            else {
                scaleX = aspect;
            }
        }
        if (this.mesh !== undefined) {
            this.mesh.scale.set(scaleX, scaleY, 1);
            this.mesh.updateMatrix();
        }
    }
}
